import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  FormControlLabel, Checkbox, TextField,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import { PageTitleContainer, RichTextEditor } from '../components/common';

// Wrapper with page-level styling for sub-pages
const SubPageWrapper = ({ children }) => (
  <Box sx={{
    width: '100%',
    bgcolor: '#f5f6f7',
    minHeight: '100vh',
    pb: 4,
    px: 6,
    pt: 4,
    overflowX: 'hidden',
    backgroundImage: 'none',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  }}>
    {children}
  </Box>
);

const CreateRoleView = ({ onCreated, editRole = null }) => {
  const [name, setName] = useState(editRole?.name || '');
  const [description, setDescription] = useState(editRole?.description || '');
  const [permissions, setPermissions] = useState(editRole?.permissions || []);
  const [isProjectRole, setIsProjectRole] = useState(editRole?.role_type === 'project');
  const [featurePerms, setFeaturePerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [permSearch, setPermSearch] = useState('');

  // Load all permissions on mount
  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const [systemFlat, projectFlat] = await Promise.all([
        permissionService.getAllPermissions('system'),
        permissionService.getAllPermissions('project'),
      ]);

      const systemPerms = systemFlat?.permissions || systemFlat?.data?.permissions || [];
      const projectPerms = projectFlat?.permissions || projectFlat?.data?.permissions || [];
      const allPerms = [...systemPerms, ...projectPerms];

      setFeaturePerms(allPerms.filter(p => p.permission_type === 'feature'));
    } catch (err) {
      console.error('Error loading permissions:', err);
    }
    setLoading(false);
  };

  // Check if a permission is selected
  const isChecked = (path) => {
    return permissions.includes(path) || permissions.includes(`${path}.*`) || permissions.includes('*');
  };

  const togglePermission = (path) => {
    setPermissions(prev => {
      // If wildcard is active and user is unchecking, expand wildcard into all individual permissions minus this one
      if (prev.includes('*')) {
        const allPaths = featurePerms.map(fp => fp.path);
        return allPaths.filter(p => p !== path);
      }
      return prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Role name is required');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editRole) {
        await roleService.updateRole(editRole.role_id || editRole.id, { name, description, permissions });
        setSuccess('Role updated successfully');
      } else {
        await roleService.createRole({ name, role_type: isProjectRole ? 'project' : 'system', description, permissions });
        setSuccess('Role created successfully');
      }
      setTimeout(() => { onCreated && onCreated(); }, 1500);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to save role');
    }
    setSaving(false);
  };

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 230px)');
  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);
  return (
    <Box>
      <PageTitleContainer
        title={editRole ? 'Edit role' : 'Create role'}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Role Details */}
      <Paper sx={{
        borderRadius: '4px',
        p: 5,
        width: '100%',
        height: contentHeight,
        bgcolor: 'white',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        backgroundImage: 'none',
        '&::before': {
          display: 'none'
        },
        '&::after': {
          display: 'none'
        }
      }}>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Role name <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField
              fullWidth size="small" value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter role name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  color: 'var(--color-blue, #475969)',
                  height: '45px',
                  transition: 'all 0.2s',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f44336',
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4759694d',
                  border: '1px solid #4759694d',
                  borderRadius: '4px',
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  padding: '12px 14px',
                  color: 'var(--color-blue, #475969)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a2abb3',
                  opacity: 0.7,
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  marginLeft: '4px',
                  fontSize: '12px'
                }
              }}
            />
          </Box>

          {/* Project role checkbox hidden — project-level permissions disabled
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isProjectRole}
                  onChange={e => setIsProjectRole(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2" fontWeight={500}>Available as project role</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
              {isProjectRole
                ? 'This role will appear in the project members dropdown when adding members to a project.'
                : 'This role is a system-level role only.'}
            </Typography>
          </Box>
          */}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>Description</Typography>
            <RichTextEditor
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter role description"
              disabled={loading}
              minHeight="150px"
              sx={{
                '& .ck-editor__editable': {
                  minHeight: '150px',
                  p: 2,
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  color: 'var(--color-blue, #475969)',
                  '& .ck-placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px'
                  }
                },
                '& .ck-toolbar': {
                  border: 'none',
                  // borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }
              }}
            />
          </Box>
        </Box>
        {/* Assign Permissions */}
        <Box sx={{ my: 4 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>Assign Permissions</Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            <Box>
              {/* Select All + Search */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox size="small"
                    checked={permissions.includes('*') || (featurePerms.length > 0 && featurePerms.every(fp => permissions.includes(fp.path)))}
                    onChange={e => {
                    const allFeaturePaths = featurePerms.map(p => p.path);
                    if (e.target.checked) {
                      setPermissions(prev => [...new Set([...prev.filter(p => p !== '*'), ...allFeaturePaths])]);
                    } else {
                      setPermissions(prev => prev.filter(p => p !== '*' && !allFeaturePaths.includes(p)));
                    }
                  }} />}
                  label={<Typography variant="body2" color="text.secondary">Select all</Typography>}
                />
                <TextField
                  size="small"
                  placeholder="Search permissions..."
                  value={permSearch}
                  onChange={e => setPermSearch(e.target.value)}
                  sx={{
                    width: "25%",
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      height: '45px',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>

              {featurePerms.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', border: '1px solid #e0e0e0', borderTop: 0 }}>
                  No permissions available
                </Box>
              )}

              {(() => {
                const groupOrder = {
                  'project.dashboard': 'Project Dashboard',
                  'project': 'Project',
                  'foundation.analysis': 'Analysis',
                  'foundation.uiux': 'UI/UX',
                  'foundation.technical': 'Technical',
                  'foundation.quality': 'Quality',
                  'foundation.infrastructure': 'Infrastructure',
                  'foundation': 'Foundation',
                  'conversation.content': 'Conversation Artifacts',
                  'conversation': 'Conversation',
                  'admin': 'Admin',
                  'header': 'Header',
                  'role': 'Roles',
                  'user': 'Users',
                };
                const groups = {};
                const filteredPerms = permSearch
                  ? featurePerms.filter(fp => fp.name.toLowerCase().includes(permSearch.toLowerCase()) || fp.path.toLowerCase().includes(permSearch.toLowerCase()))
                  : featurePerms;
                filteredPerms.forEach(fp => {
                  // Check two-level prefix first (e.g., project.dashboard), then one-level
                  const parts = fp.path.split('.');
                  const twoLevel = parts.slice(0, 2).join('.');
                  const oneLevel = parts[0];
                  let label;
                  if (groupOrder[twoLevel]) {
                    label = groupOrder[twoLevel];
                  } else {
                    label = groupOrder[oneLevel] || oneLevel.charAt(0).toUpperCase() + oneLevel.slice(1);
                  }
                  if (!groups[label]) groups[label] = [];
                  groups[label].push(fp);
                });
                const orderedKeys = Object.keys(groupOrder).map(k => groupOrder[k]).filter(k => groups[k]);
                const remainingKeys = Object.keys(groups).filter(k => !orderedKeys.includes(k));
                const allKeys = [...orderedKeys, ...remainingKeys];

                return allKeys.map((groupName, groupIdx) => {
                  const groupPerms = groups[groupName];
                  const checkedCount = groupPerms.filter(fp => isChecked(fp.path)).length;

                  return (
                    <Accordion
                      key={groupName}
                      disableGutters
                      sx={{
                        boxShadow: 'none',
                        border: 'none',
                        mb: 1,
                        borderRadius: '8px !important',
                        bgcolor: '#f5f7fa',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0, mb: 1 },
                        overflow: 'hidden',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#475969' }} />}
                        sx={{
                          bgcolor: '#f5f7fa',
                          minHeight: 52,
                          px: 2.5,
                          '&.Mui-expanded': { minHeight: 52 },
                          '& .MuiAccordionSummary-content': { my: 0, alignItems: 'center', gap: 1 },
                        }}
                      >
                        <Typography fontWeight={600} sx={{ color: '#475969', fontSize: '15px' }}>
                          {groupName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>
                          {checkedCount}/{groupPerms.length}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[...groupPerms].sort((a, b) => a.name.localeCompare(b.name)).map((fp, idx) => (
                          <Box
                            key={fp.path}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 2.5,
                              py: 1.5,
                              borderRadius: '8px',
                              backgroundColor: '#fff',
                              border: '1px solid #f0f0f0',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#475969', fontSize: '14px' }}>{fp.name}</Typography>
                            <Checkbox
                              size="small"
                              checked={isChecked(fp.path)}
                              onChange={() => togglePermission(fp.path)}
                            />
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                });
              })()}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
          <Button variant="outlined" onClick={() => onCreated && onCreated()} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none !important', bgcolor: '#333', '&:hover': { bgcolor: '#555' } }}
          >
            {saving ? <CircularProgress size={18} /> : editRole ? 'Update role' : 'Save'}
          </Button>
        </Box>
      </Paper>

    </Box>
  );
};

const CreateRolePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editRole, setEditRole] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const editId = searchParams.get('edit');

  useEffect(() => {
    if (editId) {
      setLoadingEdit(true);
      roleService.getRoleById(editId)
        .then(res => setEditRole(res.data || res))
        .catch(err => console.error('Failed to load role for editing:', err))
        .finally(() => setLoadingEdit(false));
    } else {
      setEditRole(null);
    }
  }, [editId]);

  if (loadingEdit) {
    return <SubPageWrapper><Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></SubPageWrapper>;
  }

  return <SubPageWrapper><CreateRoleView editRole={editRole} onCreated={() => navigate('/roles/manage')} /></SubPageWrapper>;
};

export default CreateRolePage;
export { CreateRoleView };
